<template>
  <v-row no-gutters>
    <!-- Spending Stat Points -->
    <v-col v-if="levelStats" cols="12">
      <v-row>
        <v-btn color="general" @click="cancelStats()">Cancel</v-btn>
        <v-spacer />
        <v-btn color="accent" @click="commitStats()">Commit</v-btn>
      </v-row>
      <br>
      <v-card color="primary">
        <v-card-text>
          <characterStats :character="statsBackup" edit class="secondary ma-2" />
        </v-card-text>
      </v-card>
    </v-col>
    <!-- Spending Skill Points -->
    <v-col v-else-if="levelSkills" cols="12">
      <v-row>
        <v-btn color="general" @click="cancelSkills()">Cancel</v-btn>
        <v-spacer />
        <v-btn color="accent" @click="commitSkills()">Commit</v-btn>
      </v-row>
      <br>
      <v-card color="primary">
        <v-card-text>
          <characterSkills :character="character" />
        </v-card-text>
      </v-card>
    </v-col>
    <!-- Spending Trait Points -->
    <v-col v-else-if="levelTraits" cols="12">
      <v-row>
        <v-btn color="general" @click="cancelTraits()">Cancel</v-btn>
        <v-spacer />
        <v-btn color="accent" @click="commitTraits()">Commit</v-btn>
      </v-row>
      <br>
      <characterTraits :character="character" />
    </v-col>
    <!-- Character View -->
    <v-col v-else cols="12">
      <!-- Name, Race, Level, XP, Stats -->
      <v-row>
        <v-col cols="12">
          <v-card dark color="primary">
            <v-card-title>
              <characterDetails :character="character" />
            </v-card-title>
            <v-card-text>
              <v-sheet color="secondary">
                <characterStats :character="character" />
                <v-row v-if="character.statPoints > 0" cols="12" justify="center">
                  <v-btn color="general" @click="setStats">Spend {{ character.statPoints }} Stat Points</v-btn>
                </v-row>
                <br>
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- Equipment -->
      <characterEquipment :character="character" />
      <!-- Skills, Traits, Misc -->
      <v-row justify="center">
        <v-col cols="12" sm="5">
          <characterInfo :character="character" />
          <br>
          <v-card dark color="primary">
            <v-card-title>
              Traits
              <v-spacer />
              <v-icon v-if="character.traitPoints > 0" @click="setTraits()">mdi-plus</v-icon>
            </v-card-title>
            <v-card-text>
              <v-sheet color="secondary" class="pa-2">
                <template v-for="(trait, i) in character.traits">
                  {{ trait }}
                  <v-divider :key="i" />
                </template>
              </v-sheet>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="7">
          <v-card dark color="primary">
            <v-card-title>
              Skills
              <v-spacer />
              <v-icon v-if="totalSkillPoints > 0" @click="setSkills()">mdi-plus</v-icon>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :items="skillList"
                :headers="skillColumns"
                hide-default-footer
                :items-per-page="skillList.length"
                disable-sort
                dark
                dense
                class="secondary">
                <template v-slot:item.ranks="{ item }">
                  {{ character.skills.values[item.name] ? character.skills.values[item.name] : 0 }}
                </template>
                <template v-slot:item.bonus="{ item }">
                  {{ skillBonus(item) }}
                </template>
                <template v-slot:item.total="{ item }">
                  {{ skillTotal(item) }}
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import _ from 'lodash';
import { mapGetters } from 'vuex';

export default {
  components: {
    characterSkills: () => import('./characterSkills'),
    characterStats: () => import('./characterStats'),
    characterTraits: () => import('./characterTraits'),
    characterDetails: () => import('./characterDetails'),
    characterEquipment: () => import('./characterEquipment'),
    characterInfo: () => import('./characterInfo')
  },
  props: {
    character: {
      type: Object,
      required: true
    },
    chosenName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      levelSkills: false,
      levelTraits: false,
      levelStats: false,
      statsBackup: {},
      skillSubsets:[
        {
          name: 'Craft(Alchemy)',
          stat: 'Mind'
        },
        {
          name: 'Craft(Artistry)',
          stat: 'Mind'
        },
        {
          name: 'Craft(Blacksmith)',
          stat: 'Mind'
        },
        {
          name: 'Craft(Woodworking)',
          stat: 'Mind'
        },
        {
          name: 'Knowledge(Arcana)',
          stat: 'Mind'
        },
        {
          name: 'Knowledge(History)',
          stat: 'Mind'
        },
        {
          name: 'Knowledge(Language)',
          stat: 'Mind'
        },
        {
          name: 'Knowledge(Religion)',
          stat: 'Mind'
        },
        {
          name: 'Knowledge(Research)',
          stat: 'Mind'
        }
      ],
      skillColumns: [
        {
          text: 'Skill',
          value: 'name'
        },
        {
          text: 'Ranks',
          value: 'ranks'
        },
        {
          text: 'Bonus',
          value: 'bonus'
        },
        {
          text: 'Total',
          value: 'total'
        }
      ]
    };
  },
  watch: {
    chosenName() {
      this.levelSkills = false;
      this.levelStats = false;
      this.levelTraits = false;
    }
  },
  methods: {    
    // When the user chooses to spend skills set resetSkills equal to what they're starting with and change the display
    setSkills() {
      this.character.resetSkills = _.cloneDeep(this.character.skills);
      this.levelSkills = true;
    },
    // If they cancel the changes then reset the character skills to the backed up 'resetSkills'
    cancelSkills() {
      this.character.skills = _.cloneDeep(this.character.resetSkills);
      this.levelSkills = false;
    },
    // If they commit their changes then set their reset Skills to equal the new changes
    commitSkills() {
      this.character.resetSkills = _.cloneDeep(this.character.skills);
      this.levelSkills = false;
    },
    // When the user elects to spend stat points create a copy of the character to change of the character and change the display
    setStats() {
      this.statsBackup = _.cloneDeep(this.character);
      this.levelStats = true;
    },
    cancelStats() {
      this.levelStats = false;
    },
    // If the changes are confirmed then update the characters stats
    commitStats() {
      this.character.physique = this.statsBackup.physique;
      this.character.finesse = this.statsBackup.finesse;
      this.character.spirit = this.statsBackup.spirit;
      this.character.mind = this.statsBackup.mind;
      this.character.statPoints = this.statsBackup.statPoints;
      this.levelStats = false;
    },
    // When the user elects to spend trait points clear out the 'resetTraits' for future use and change the display
    setTraits() {
      this.character.resetTraits = [];
      this.levelTraits = true;
    },
    cancelTraits() {
      this.levelTraits = false;
    },
    commitTraits() {
      // Deduct the amount of trait points by the amount of new traits selected
      this.character.traitPoints -= this.character.resetTraits.length;
      // Combine the existing array of traits with the newly confirmed traits
      this.character.traits = this.character.traits.concat(this.character.resetTraits);
      // Clear out the reset traits again
      this.character.resetTraits = [];
      this.levelTraits = false;
    },
    skillBonus(skill) {
      let bonus = 0;
      if (skill.stat === 'Physique' && this.character.traits.includes('Mighty')) {
        bonus += 1;
      }
      if (skill.stat === 'Finesse' && this.character.traits.includes('Limber')) {
        bonus += 1;
      }
      if (skill.stat === 'Mind' && this.character.traits.includes('Clever')) {
        bonus += 1;
      }
      if (skill.stat === 'Spirit' && this.character.traits.includes('Spiritual')) {
        bonus += 1;
      }
      if (!skill.name.includes('Attack') && this.character.traits.includes('Natural')) {
        bonus += 1;
      }
      return bonus;
    },
    skillTotal(skill) {
      return  (this.character.skills.values[skill.name] ? this.character.skills.values[skill.name] : 0) + this.skillBonus(skill);
    }
  },
  computed: {
    ...mapGetters('actsContent', ['contentItem']),
    skillList() {      
    return this.contentItem('table|7-1').items.concat(this.skillSubsets).concat(this.character.skills.custom)
      // eslint-disable-next-line no-nested-ternary
      .sort((a,b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)).filter(skill => !skill.name.includes('()'));
    },
    totalSkillPoints() {
      const sum = this.character.skills.skillPoints.physique
        + this.character.skills.skillPoints.finesse
        + this.character.skills.skillPoints.mind
        + this.character.skills.skillPoints.spirit
        + this.character.skills.skillPoints.specialty
        + this.character.skills.skillPoints.general;

      return sum;
    }
  }
};
</script>
